import { Params } from '@angular/router';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MaintainService } from '../maintain.service';
import {
  ActionConfigDTO,
  CassandraConfigDTO,
  FieldConfigDTO,
  FormRowConfigDTO,
  MaintainTabDTO,
  MenuDTO,
  RowConditionDTO,
} from '../../../../system/system-dtos';
import { PageRequestDTO, PageResponseDTO } from '../../../../common/paging-dto';
import { QueryConditionDTO, QueryOperand } from '../../../../common/kibana-request-dto';
import { ExtractedActions } from '../../../../common/event-dtos';
import { DataSource } from './data-source';
import { EncodingUtils } from '../../../../common/encoding-utils';
import { MenuUtils } from '../../../../common/utils/menu-utils';
import { CassandraQueryDTO } from '../../../../common/cassandra-request-dto';

export class CassandraDataSource extends DataSource {
  public constructor(private maintainService: MaintainService) {
    super();
  }

  public static buildQuery(
    menuConfig: MenuDTO,
    pageRequest: PageRequestDTO,
    filters: { [p: string]: FieldConfigDTO },
    id: any,
    context: any,
    queryParams: Params,
    currentTab: MaintainTabDTO
  ): CassandraQueryDTO {
    const { currentMode } = menuConfig.entityGroup;
    const cassandraConfig = currentMode
      ? MenuUtils.getNthConfigAsCassandra(menuConfig.entityGroup, currentMode)
      : MenuUtils.getFirstCassandraConfig(menuConfig.entityGroup);

    const cassandraQuery: CassandraQueryDTO = {
      offset: pageRequest.offset,
      perPage: pageRequest.perPage,
      convertTo: cassandraConfig.convertTo,
      pageStates: pageRequest.pageStates,
      queryConditions: {
        index: '',
        conditions: this.createConditions(filters, cassandraConfig, id, context),
      },
      orderParamTieBreaker: (currentTab || cassandraConfig.tabs[0]).tableView.sortTieBreaker,
      after: queryParams.after,
      before: queryParams.before,
      last: queryParams.last,
    };

    if (pageRequest.order && !pageRequest.order.includes('null')) {
      const orderStringSplit = pageRequest.order.split(',');
      cassandraQuery.orderParam = orderStringSplit[0];
      cassandraQuery.orderDirection = orderStringSplit[1];
    }
    return cassandraQuery;
  }

  private static createConditions(
    filters: { [p: string]: FieldConfigDTO },
    cassandraConfig: CassandraConfigDTO,
    entityId: number,
    context: any
  ): QueryConditionDTO[] {
    const initialFilters = this.doGetInitialFilters(cassandraConfig.tabs[0].initialConditions, entityId, context);
    const mergedFilters = { ...filters, ...initialFilters };

    const arr: QueryConditionDTO[] = [];
    Object.keys(mergedFilters)
      .map((k) => mergedFilters[k])
      .filter((mf) => mf.value || mf.value === 0)
      .forEach((mf) => {
        arr.push({
          fieldName: EncodingUtils.camelToKebabCase(mf.filterCassandraName),
          // fieldName: EncodingUtils.camelToKebabCase(mf.filterElasticName),
          operand: mf.cassandraSearchOperand ? mf.cassandraSearchOperand : QueryOperand.EQUALS,
          value: DataSource.getObjectIfJsonOrString(mf.value),
        });
      });
    return arr;
  }

  static doGetInitialFilters(initialConditions: RowConditionDTO[], entityId: number, context: any) {
    const filters = {};
    if (!initialConditions || initialConditions.length === 0) {
      return filters;
    }

    initialConditions.forEach((condition) => {
      filters[condition.field] = {
        value: this.convertValue(condition, entityId, context),
        filterCassandraName: EncodingUtils.camelToKebabCase(condition.field),
        cassandraSearchOperand: condition.operand,
      };
    });

    return filters;
  }

  static convertValue(condition, entityId: number, context: any) {
    const entity = context;
    const header = context;
    return eval(condition.value.replace('{entityId}', entityId));
  }

  public getTabs(menuConfig: MenuDTO): MaintainTabDTO[] {
    return MenuUtils.getFirstCassandraConfig(menuConfig.entityGroup).tabs;
  }

  public getRows(menuConfig: MenuDTO): FormRowConfigDTO[] {
    return MenuUtils.getFirstCassandraConfig(menuConfig.entityGroup).rows;
  }

  public doGetData(
    menuConfig: MenuDTO,
    currentTab: MaintainTabDTO,
    pageRequest: PageRequestDTO,
    filters: { [p: string]: FieldConfigDTO },
    id: any,
    context: any = {},
    queryParams: Params
  ): Observable<PageResponseDTO<any>> {
    const query = CassandraDataSource.buildQuery(
      menuConfig,
      pageRequest,
      filters,
      id,
      context,
      queryParams,
      currentTab
    );

    const timerStart = performance.now();
    let selectUrl =
      currentTab.endpoints && currentTab.endpoints.selectAllUrl
        ? currentTab.endpoints.selectAllUrl
        : MenuUtils.getFirstCassandraConfig(menuConfig.entityGroup).selectUrl;
    selectUrl = selectUrl.replace('{id}', id);
    Object.keys(context).forEach((k) => {
      selectUrl = selectUrl.replace(`{${k}}`, context[k]);
    });

    return this.maintainService
      .performCassandraQueryWithUrl(query, selectUrl)
      .pipe(tap((d) => (d.executionMillis = ((performance.now() - timerStart) / 1000).toFixed(2))));
  }

  getTitle(menuConfig: MenuDTO): string {
    return menuConfig.label;
  }

  extractActions(menuConfig: MenuDTO, tab: MaintainTabDTO, element: any): ExtractedActions {
    return {
      rest: MenuUtils.getFirstCassandraConfig(menuConfig.entityGroup).restActions,
      simple: [],
    };
  }

  getTabsWithCount(menuConfig: MenuDTO): Observable<{ [p: string]: number }> {
    return new Observable<{ [p: string]: number }>((observer) => {
      observer.next(null);
      observer.complete();
    });
  }

  override getNoteForData(menu: MenuDTO): string {
    return '';
  }

  getOverallRestActions(menu: MenuDTO): ActionConfigDTO[] {
    return MenuUtils.getFirstCassandraConfig(menu.entityGroup).overallRestActions;
  }
}
